import React, { useState, useRef } from "react";
import { Box, Typography, Button, Grid, FormControl, FormLabel, TextField, IconButton, Select, MenuItem } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import styled from "styled-components";
import { postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const GRADE_OPTIONS = ["Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8",
  "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4", "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8"];

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#D5D7DA", borderRadius: "10px" },
  },
}));

const labelStyle = { color: "#8F95B2", fontSize: "13px" };

export default function ReImbursementForm({ handleClose }) {
  const teacherName = sessionStorage.getItem("userFullName") || "Teacher";
  const teacherEmail = sessionStorage.getItem("userEmail") || "";
  const [form, setForm] = useState({
    teacherName,
    teacherEmail,
    empId: "",
    grade: "",
    section: "",
    expenseTitle: "",
    amount: "",
    date: "",
    description: "",
  });
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const fileRef = useRef(null);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    ["teacherName", "empId", "expenseTitle", "amount", "date"].forEach((k) => {
      if (!form[k]) newErrors[k] = "Required";
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      const formData = new FormData();
      Object.keys(form).forEach((k) => formData.append(k, form[k]));
      if (file) formData.append("receipt", file);
      await postData("teacher/reimbursement/create", formData);
      setToast({ open: true, severity: "success", message: "Reimbursement submitted successfully" });
      setTimeout(handleClose, 1200);
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Submission failed" });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: "24px" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
        <Box>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>{teacherName}</Typography>
          <Typography sx={{ color: "#8F95B2", fontSize: "13px" }}>Labels help organize projects.</Typography>
        </Box>
        <IconButton onClick={handleClose}><CloseIcon /></IconButton>
      </Box>

      <Typography sx={{ fontWeight: "bold", fontSize: "13px", color: "#244C77", mb: 1.5, mt: 1 }}>Teacher Info</Typography>

      <Grid container spacing={2}>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Teachers Name</FormLabel>
            <StyledTextField size="small" name="teacherName" value={form.teacherName} onChange={handleChange} error={!!errors.teacherName} helperText={errors.teacherName} />
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Teacher Email ID</FormLabel>
            <StyledTextField size="small" name="teacherEmail" type="email" value={form.teacherEmail} onChange={handleChange} placeholder="teacher@school.org" />
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Emp.ID</FormLabel>
            <StyledTextField size="small" name="empId" value={form.empId} onChange={handleChange} placeholder="EMP001" error={!!errors.empId} helperText={errors.empId} />
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Grade</FormLabel>
            <Select size="small" name="grade" value={form.grade} onChange={handleChange} sx={{ borderRadius: "10px" }}>
              {GRADE_OPTIONS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Section</FormLabel>
            <StyledTextField size="small" name="section" value={form.section} onChange={handleChange} placeholder="e.g. A" />
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Expense Title</FormLabel>
            <StyledTextField size="small" name="expenseTitle" value={form.expenseTitle} onChange={handleChange} error={!!errors.expenseTitle} helperText={errors.expenseTitle} />
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Amount</FormLabel>
            <StyledTextField size="small" name="amount" type="number" value={form.amount} onChange={handleChange} placeholder="0.00" error={!!errors.amount} helperText={errors.amount} />
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Date</FormLabel>
            <StyledTextField size="small" name="date" type="date" value={form.date} onChange={handleChange} InputLabelProps={{ shrink: true }} error={!!errors.date} helperText={errors.date} />
          </FormControl>
        </Grid>
        <Grid item xs={4}>
          {/* spacer */}
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Description</FormLabel>
            <StyledTextField size="small" name="description" multiline rows={3} value={form.description} onChange={handleChange} placeholder="Describe the expense..." />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Box
            onClick={() => fileRef.current?.click()}
            onDrop={(e) => { e.preventDefault(); setFile(e.dataTransfer.files[0]); }}
            onDragOver={(e) => e.preventDefault()}
            sx={{ border: "2px dashed #D5D7DA", borderRadius: "10px", p: "24px", textAlign: "center", cursor: "pointer", backgroundColor: "#fafafa" }}
          >
            <input type="file" ref={fileRef} style={{ display: "none" }} accept=".pdf,.jpg,.png" onChange={(e) => setFile(e.target.files[0])} />
            <CloudUploadOutlinedIcon sx={{ fontSize: "40px", color: "#8F95B2" }} />
            <Typography sx={{ color: "#8F95B2", fontSize: "13px", mt: 1 }}>
              {file ? file.name : "Click to upload receipt or drag and drop"}
            </Typography>
            <Typography sx={{ color: "#bbb", fontSize: "11px" }}>PDF, PNG, JPG (max. 5MB)</Typography>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
        <Button onClick={handleClose} sx={{ color: "#414651", textTransform: "none", px: 4, borderRadius: "10px", border: "1px solid #D5D7DA" }}>Cancel</Button>
        <Button type="submit" sx={{ backgroundColor: "#1a237e", color: "#fff", textTransform: "none", px: 4, borderRadius: "10px", "&:hover": { backgroundColor: "#283593" } }}>Submitted</Button>
      </Box>
    </Box>
  );
}
